import {
  Box,
  styled,
  Card,
  Typography,
  useTheme,
  CardContent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { forwardRef } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { getColorFromString } from '@ui-framework/index';

export const Circle = styled(Box, {
  shouldForwardProp: props => !['bgColor', 'size'].includes(props),
})(({ theme, bgColor, size }) => ({
  backgroundColor: bgColor,
  padding: theme.spacing(1),
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export function IconChip({
  bgColor = 'primary.light',
  iconColor = 'primary.main',
  Icon,
  size = 40,
  iconFontSize = null,
}) {
  const theme = useTheme();

  const bgColorValue = getColorFromString(theme.palette, bgColor);
  const iconColorValue = getColorFromString(theme.palette, iconColor);

  let fontSize = `${size / 2.2}px`;
  if (iconFontSize) {
    fontSize = iconFontSize;
  }

  return (
    <Circle size={size} bgColor={bgColorValue || bgColor}>
      <Icon sx={{ color: iconColorValue || iconColor, fontSize }} />
    </Circle>
  );
}

export const Container = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
}));

function SectionTitle({ Icon = null, children, subSection = false }) {
  return (
    <Box display="flex" alignItems="center">
      {Icon && !subSection && (
        <IconChip
          bgColor="primary.light"
          iconColor="primary.main"
          Icon={Icon}
          size={40}
        />
      )}
      {Icon && subSection && (
        <Icon sx={{ backgrooundColor: 'transparent', color: 'primary.main' }} />
      )}

      <Typography
        variant="h6"
        component="span"
        color="primary.main"
        sx={{
          fontWeight: 500,
          ...(Icon && { marginLeft: '10px' }),
          ...(subSection && { fontSize: '18px' }),
        }}>
        {children}
      </Typography>
    </Box>
  );
}

const Line = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  height: '1px',
  display: 'block',
  margin: `-5px ${theme.spacing(2)} auto`,
}));

function Seperator() {
  return <Line />;
}

export function CardHeaderTitleWithIcon({
  label,
  localeId = null,
  headerIcon,
  subSection,
}) {
  return (
    <SectionTitle subSection={subSection} Icon={headerIcon}>
      {localeId ? (
        <FormattedMessage id={localeId} defaultMessage={label} />
      ) : (
        label
      )}
    </SectionTitle>
  );
}

const PageSection = forwardRef(
  (
    {
      label = null,
      localeId = null,
      headerIcon = null,
      children,
      id = null,
      HeaderContent = null,
      subSection = false,
      containerProps = null,
      headerProps = {},
      ...props
    },
    ref,
    // eslint-disable-next-line arrow-body-style
  ) => {
    return (
      <Card
        {...(id && { id })}
        ref={ref}
        sx={{ my: 1 }}
        {...containerProps}
        elevation={0}
        {...props}>
        <CardContent
          className="cardContent"
          {...headerProps}
          sx={{
            pt: 0.6,
            paddingBottom: '10px !important',
            px: 1.6,
          }}>
          {(localeId || label || HeaderContent) && (
            <Grid2 container sx={{ mb: 1.5, ...(headerProps?.sx || {}) }}>
              <Grid2 xs={6}>
                {(localeId || label) && (
                  <CardHeaderTitleWithIcon
                    label={label}
                    localeId={localeId}
                    headerIcon={headerIcon}
                    subSection={subSection}
                  />
                )}
              </Grid2>
              <Grid2 sx={{ pr: 0.5 }} alignSelf="center" xs={6}>
                {HeaderContent}
              </Grid2>
            </Grid2>
          )}
          {children}
        </CardContent>
      </Card>
    );
  },
);

export function Section({
  label = null,
  localeId = null,
  headerIcon = null,
  children,
  headerContent = null,
  contentProps = {},
}) {
  return (
    <Card sx={{ width: '100%' }}>
      <Box
        sx={{
          padding: {
            xs: 1,
            sm: 2,
          },
        }}>
        <Grid2 container>
          <Grid2 xs={8}>
            <CardHeaderTitleWithIcon
              localeId={localeId}
              label={label}
              headerIcon={headerIcon}
            />
          </Grid2>
          {headerContent && (
            <Grid2 sx={{ pr: 2 }} alignSelf="center" xs={4}>
              {headerContent}
            </Grid2>
          )}
        </Grid2>
      </Box>
      <Seperator />
      <Box sx={{ p: 2 }} {...contentProps}>
        {children}
      </Box>
    </Card>
  );
}

export default PageSection;
